import { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/haptic-toast';
import { isNative } from '@/lib/capacitor';
import {
  addCustomerInfoListener,
  reloadServerEntitlements,
} from '@/lib/revenuecat';
import {
  claimPendingPurchase,
  hasPendingPurchase,
  subscribePendingPurchase,
} from '@/lib/pending-store-purchase';
import { replaceVisit } from '@/lib/replace-visit';

function samePath(url, other) {
  if (!url || !other) return false;
  const { origin } = window.location;
  return new URL(url, origin).pathname === new URL(other, origin).pathname;
}

// Lands a deferred store purchase (iOS Ask to Buy, a pending Play payment)
// when the store completes it — often hours later, with the app backgrounded
// or closed and the paywall long gone. Same landing as an immediate purchase
// (docs/roadmap/0216): once the server confirms Plus, toast and replace the
// current page with a new chat. Mounted once, in the app shell
// (entrypoints/inertia.jsx), so it survives every visit; a no-op on web and
// while no deferred purchase is remembered for the signed-in account.
export default function usePendingPurchaseLanding(initialPage) {
  const { t } = useTranslation();
  // Read at landing time, so neither a language change nor a fresh `t` per
  // render restarts the watch.
  const translate = useRef(t);
  translate.current = t;
  const page = useRef(initialPage);
  const [userId, setUserId] = useState(
    () => initialPage?.props?.currentUser?.id ?? null,
  );
  // Whether the current page's server-rendered props already grant AI access
  // (Plus): any visit can carry the approval, not only the first page.
  const [grantsAi, setGrantsAi] = useState(
    () => initialPage?.props?.currentUser?.aiAccess === true,
  );
  // The account the remembered deferred purchase belongs to, kept current by
  // the store's notifications (deferred in this session, claimed, cleared).
  const [pendingFor, setPendingFor] = useState(() =>
    isNative() && hasPendingPurchase(userId) ? userId : null,
  );
  const pending = userId != null && pendingFor === userId;

  useEffect(
    () =>
      router.on('navigate', (event) => {
        const currentUser = event.detail.page.props?.currentUser;
        page.current = event.detail.page;
        setUserId(currentUser?.id ?? null);
        setGrantsAi(currentUser?.aiAccess === true);
      }),
    [],
  );

  useEffect(() => {
    if (!isNative() || userId == null) return undefined;
    const sync = () =>
      setPendingFor(hasPendingPurchase(userId) ? userId : null);
    sync();
    return subscribePendingPurchase(sync);
  }, [userId]);

  useEffect(() => {
    if (!pending) return undefined;
    let active = true;
    let checking = null;
    let recheck = false;
    const removers = [];

    function land(confirmedByServer) {
      const { url, props } = page.current || {};
      const newChat = props?.routes?.newChat;
      toast.success(translate.current('billing.purchaseSuccess'));
      if (newChat && !samePath(url, newChat)) {
        replaceVisit(newChat);
      } else if (confirmedByServer) {
        // Already in new chat (or nowhere to go): refresh its stale props.
        router.reload();
      }
    }

    async function confirm() {
      if (!active) return;
      // Expired (72 h), or claimed by a paywall's Check status: stop watching.
      if (!hasPendingPurchase(userId)) {
        setPendingFor(null);
        return;
      }
      const result = await reloadServerEntitlements();
      const premium = (result?.entitlements || []).some(
        (e) => e.key === 'premium',
      );
      if (active && premium && claimPendingPurchase(userId)) land(true);
    }

    // One server round-trip at a time: a resume and the store's own refresh
    // on foreground arrive together. A trigger that arrives while a check is
    // running is not dropped, though: the store can report Plus after that
    // check already asked RevenueCat (the SDK posts the receipt on
    // foreground), and RevenueCat's listener will not fire again. So once the
    // running check settles, check once more — confirm() stops at once if it
    // landed or the flag is gone.
    function check() {
      if (checking) {
        recheck = true;
        return checking;
      }
      checking = (async () => {
        do {
          recheck = false;
          await confirm();
        } while (recheck);
      })().finally(() => {
        checking = null;
      });
      return checking;
    }

    function keep(remove) {
      if (!remove) return;
      if (active) removers.push(remove);
      else remove();
    }

    // Check as soon as watching starts (cold start, sign-in, a purchase just
    // deferred): the approval may have landed while the app was closed, and a
    // page that already grants AI access says so without a round-trip — on
    // the first page or on any later visit (which restarts this effect).
    if (grantsAi) {
      if (claimPendingPurchase(userId)) land(false);
      return undefined;
    }
    check();

    // 'resume', not appStateChange: only a genuine return from the background
    // (see @/lib/capacitor and @/lib/push-notifications).
    import('@capacitor/app')
      .then(({ App }) => App.addListener('resume', check))
      .then((handle) => keep(() => handle.remove()))
      .catch(() => {});
    // Fires when StoreKit / Play deliver the completed transaction while the
    // app runs, and replays the last known info on subscribe — so only ask
    // the server once the store itself reports Plus.
    addCustomerInfoListener(userId, (customerInfo) => {
      if (customerInfo?.entitlements?.active?.premium) check();
    })
      .then(keep)
      .catch(() => {});

    return () => {
      active = false;
      removers.forEach((remove) => remove());
    };
  }, [pending, userId, grantsAi]);
}
